import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import Icon from '../common/Icons';
import { AdminPageHeader } from './ui';
import CompanyService from '../../services/companyService';
import AffiliationSelect from '../companies/AffiliationSelect';
import CompanyListTable from '../companies/CompanyListTable';
import CompanyCitySelect from '../companies/CompanyCitySelect';
import useSmieAffiliations from '../../hooks/useSmieAffiliations';
import useCompanyCities from '../../hooks/useCompanyCities';
import useCompanyListFilters from '../../hooks/useCompanyListFilters';
import CompanyListStats from '../companies/CompanyListStats';
import CompanyListFilters from '../companies/CompanyListFilters';
import {
  buildCompanyPayload,
  buildEmptyCompanyForm,
  companyFormFromRecord
} from '../companies/companyListUtils';
import CompanyRowActionMenu from './CompanyRowActionMenu';
import './CompanyManagement.css';
import './AdminPortal.css';

const CompanyManagement = () => {
  const { toast } = useOutletContext() || { toast: () => {} };
  const {
    affiliations,
    loading: affiliationsLoading,
    defaultAffiliation,
    resolveForCompany,
    reload: reloadAffiliations,
    error: affiliationsError
  } = useSmieAffiliations();

  const {
    cities,
    loading: citiesLoading,
    defaultCity,
    reload: reloadCities,
    error: citiesError
  } = useCompanyCities();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState(() => buildEmptyCompanyForm());
  const [actionMenuCompany, setActionMenuCompany] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CompanyService.getAllCompanies();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des entreprises:', err);
      toast('danger', err.message || 'Impossible de charger les sociétés');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    affiliationFilter,
    setAffiliationFilter,
    cityFilter,
    setCityFilter,
    filteredCompanies,
    stats,
    hasActiveFilters,
    clearFilters
  } = useCompanyListFilters(companies, { affiliations, cities });

  const closeActionMenu = useCallback(() => {
    setActionMenuCompany(null);
    setActionMenuAnchor(null);
  }, []);

  const openActionMenu = (event, company) => {
    if (actionMenuCompany?.id === company.id && actionMenuAnchor) {
      closeActionMenu();
      return;
    }
    setActionMenuAnchor(event.currentTarget);
    setActionMenuCompany(company);
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setFormData(buildEmptyCompanyForm(defaultAffiliation, defaultCity));
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData(companyFormFromRecord(company, affiliations, defaultAffiliation, cities, defaultCity));
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_code || formData.company_code.trim().length < 2) {
      toast('warning', 'Le code société est obligatoire (2 caractères minimum).');
      return;
    }
    if (!formData.name || formData.name.trim().length < 2) {
      toast('warning', 'Le nom de la société est obligatoire (2 caractères minimum).');
      return;
    }
    if (!formData.affiliation_id) {
      toast('warning', 'Sélectionnez une affiliation SMIE.');
      return;
    }

    const payload = buildCompanyPayload(formData);

    try {
      if (editingCompany) {
        await CompanyService.updateCompany(editingCompany.id, payload);
        toast('success', 'Société mise à jour avec succès.');
      } else {
        await CompanyService.createCompany(payload);
        toast('success', 'Société créée avec succès.');
      }
      setShowModal(false);
      await loadCompanies();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      toast('danger', err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette société ?')) return;
    try {
      await CompanyService.deleteCompany(id);
      toast('success', 'Société supprimée.');
      closeActionMenu();
      await loadCompanies();
    } catch (err) {
      toast('danger', err.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (company) => {
    try {
      if (company.is_active) {
        await CompanyService.deactivateCompany(company.id);
        toast('success', `« ${company.name} » a été désactivée.`);
      } else {
        await CompanyService.updateCompany(company.id, { is_active: true });
        toast('success', `« ${company.name} » a été réactivée.`);
      }
      closeActionMenu();
      await loadCompanies();
    } catch (err) {
      toast('danger', err.message || 'Erreur lors du changement de statut');
    }
  };

  const hasCompanies = filteredCompanies.length > 0;

  return (
    <div className="adm-dash company-management-v2">
      <AdminPageHeader
        badge="Administration"
        title="Gestion des sociétés"
        description="Répertoire des sociétés adhérentes — affiliations SMIE configurables en base."
        actions={
          <button type="button" className="co-create-btn" onClick={handleCreate}>
            <Icon name="plus" size={14} />
            <span>Nouvelle société</span>
          </button>
        }
      />

      <section className="co-shell adm-panel">
        <CompanyListStats stats={stats} />

        <CompanyListFilters
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          affiliationFilter={affiliationFilter}
          onAffiliationFilterChange={setAffiliationFilter}
          affiliationOptions={affiliations}
          cityFilter={cityFilter}
          onCityFilterChange={setCityFilter}
          cities={cities}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          onRefresh={loadCompanies}
          loading={loading}
          filteredCount={filteredCompanies.length}
          repositoryTotal={companies.length}
        />

        {loading ? (
          <div className="co-state">
            <Spinner animation="border" variant="primary" />
            <p>Chargement du répertoire…</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="co-state co-state--empty">
            <div className="co-state__icon">
              <Icon name="building" size={36} />
            </div>
            <h3>Aucune société enregistrée</h3>
            <p>Créez la première entreprise adhérente SMIE.</p>
            <button type="button" className="co-create-btn" onClick={handleCreate}>
              <Icon name="plus" size={14} />
              <span>Créer une société</span>
            </button>
          </div>
        ) : !hasCompanies ? (
          <div className="co-state">
            <Icon name="inbox" size={40} className="mb-2 opacity-50" />
            <h6>Aucun résultat</h6>
            <p>Modifiez la recherche ou réinitialisez les filtres.</p>
            {hasActiveFilters ? (
              <button type="button" className="co-list-clear-btn mt-2" onClick={clearFilters}>
                Réinitialiser les filtres
              </button>
            ) : null}
          </div>
        ) : (
          <div key={`${search}-${statusFilter}-${affiliationFilter}-${cityFilter}`}>
            <CompanyListTable
              companies={filteredCompanies}
              resolveForCompany={resolveForCompany}
              cities={cities}
              activeActionCompanyId={actionMenuCompany?.id}
              onOpenActionMenu={openActionMenu}
            />
          </div>
        )}
      </section>

      <CompanyRowActionMenu
        anchorEl={actionMenuAnchor}
        company={actionMenuCompany}
        cities={cities}
        resolveForCompany={resolveForCompany}
        onClose={closeActionMenu}
        onEdit={handleEdit}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
      />

      {showModal && (
        <div className="co-modal-overlay" role="presentation" onClick={() => setShowModal(false)}>
          <div
            className="co-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="co-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="co-modal__header">
              <div>
                <span className="co-modal__badge">
                  {editingCompany ? 'Modification' : 'Nouvelle fiche'}
                </span>
                <h2 id="co-modal-title">
                  {editingCompany ? 'Modifier la société' : 'Créer une société'}
                </h2>
              </div>
              <button type="button" className="co-modal__close" onClick={() => setShowModal(false)} aria-label="Fermer">
                <Icon name="times" size={18} />
              </button>
            </header>

            <form onSubmit={handleSubmit} className="co-modal__body">
              <div className="co-form-grid">
                <div className="co-form-group co-form-group--full">
                  <label htmlFor="co-affiliation-modal">Affiliation SMIE *</label>
                  <AffiliationSelect
                    id="co-affiliation-modal"
                    value={formData.affiliation_id}
                    affiliations={affiliations}
                    defaultAffiliation={defaultAffiliation}
                    loading={affiliationsLoading}
                    loadError={affiliationsError}
                    onReloadAffiliations={reloadAffiliations}
                    onChange={(e) =>
                      setFormData({ ...formData, affiliation_id: e.target.value })
                    }
                  />
                </div>

                <div className="co-form-group">
                  <label htmlFor="co-company-code">Code société *</label>
                  <input
                    type="text"
                    id="co-company-code"
                    name="company_code"
                    value={formData.company_code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        company_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')
                      })
                    }
                    required
                    placeholder="FT001"
                    maxLength={32}
                    autoFocus
                  />
                </div>

                <div className="co-form-group co-form-group--full">
                  <label htmlFor="co-name">
                    <Icon name="building" size={14} />
                    Nom de la société *
                  </label>
                  <input
                    type="text"
                    id="co-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="FUNTOA SMIE"
                  />
                </div>

                <div className="co-form-group">
                  <label htmlFor="co-city">
                    <Icon name="building" size={14} />
                    Ville
                  </label>
                  <CompanyCitySelect
                    id="co-city"
                    value={formData.city_id}
                    cities={cities}
                    defaultCity={defaultCity}
                    loading={citiesLoading}
                    loadError={citiesError}
                    onReloadCities={reloadCities}
                    onChange={(e) =>
                      setFormData({ ...formData, city_id: e.target.value })
                    }
                  />
                </div>

                <div className="co-form-group">
                  <label htmlFor="co-phone">
                    <Icon name="phone" size={14} />
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="co-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+261 …"
                  />
                </div>

                <div className="co-form-group co-form-group--full">
                  <label htmlFor="co-address">Adresse</label>
                  <input
                    type="text"
                    id="co-address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Rue, quartier, zone industrielle…"
                  />
                </div>

                <div className="co-form-group co-form-group--full">
                  <label htmlFor="co-mail">
                    <Icon name="envelope" size={14} />
                    Email
                  </label>
                  <input
                    type="email"
                    id="co-mail"
                    value={formData.mail}
                    onChange={(e) => setFormData({ ...formData, mail: e.target.value })}
                    placeholder="contact@societe.mg"
                  />
                </div>
              </div>

              <footer className="co-modal__footer">
                <button type="button" className="co-btn-ghost" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="co-btn-primary">
                  {editingCompany ? 'Enregistrer' : 'Créer la société'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
